"use client";

import {
  bannerProperties,
  mediaType,
  promotionalBannerType,
} from "@/apiUtils/constants";
import NoImageSrc from "@/assets/images/no_image.svg";
import {
  MultipleColBanner,
  PromotionalBannerItem,
  PromotionalBannerProperties,
  PromotionalBannerTypes,
  SingleColBanner,
} from "@/common/lib/types";
import ImageCard from "@/components/common/imageCard/ImageCard";
import VideoCard from "@/components/common/videoCard/VideoCard";
import Button from "@/components/ui/button/Button";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { buildGradientOverlayCss } from "@/utils/gradientOverlay";
import { getFieldValue, getLangFromLocale } from "@/utils/common";
import { useCallback } from "react";
import { ICategoryData } from "../../clp/types";


const Promotions = ({
  promotionalData,
  locale,
  handleClick,
  handleOnVisible,
}: {
  promotionalData: ICategoryData["promotionalData"];
  locale: string;
  handleOnVisible: (isVisible: boolean, data: PromotionalBannerItem) => void;
  handleClick: (data: PromotionalBannerItem) => void;
}) => {
  const lang = getLangFromLocale(locale);

  const renderBanner = useCallback(
    (content: SingleColBanner | MultipleColBanner, index: number) => {
      const bannerType = content?.type;

      // SINGLE COL
      if (
        [
          promotionalBannerType.SINGLE_COL_LARGE_BANNER,
          promotionalBannerType.SINGLE_COL_LARGE_BANNER_MOBILE,
          promotionalBannerType.SINGLE_COL_MEDIUM_BANNER,
          promotionalBannerType.SINGLE_COL_MEDIUM_BANNER_MOBILE,
          promotionalBannerType.SINGLE_COL_SMALL_BANNER,
        ].includes(bannerType)
      ) {
        const properties: PromotionalBannerProperties =
          bannerProperties[bannerType as PromotionalBannerTypes];
        const banner = content as SingleColBanner;

        const hasOverlay = banner?.bannerVariant === "overlay";
        const overlayBackground = hasOverlay
          ? buildGradientOverlayCss(banner?.overlay)
          : undefined;

        return (
          <div key={`singleColWrapper_${index}`} className={properties?.className}>
            {banner.mediaType === mediaType.IMAGE ? (
              <ImageCard
                url={banner?.link?.url}
                containerProps={{
                  className: cn("h-full w-full", hasOverlay && "relative"),
                  style: {
                    ...(banner?.color && { backgroundColor: banner.color }),
                    ...(hasOverlay && { background: overlayBackground }),
                  },
                }}
                action={{
                   cta: banner?.linkTitle && (
                    <Button
                      className={cn(
                        "font-semibold",
                        banner?.linkVariant === "link"
                          ? "text-xs md:text-sm"
                          : "flex h-[36px] min-w-[120px] items-center justify-center p-0 text-sm md:h-[50px] md:min-w-[170px]",
                      )}
                      variant={banner?.linkVariant ?? "text"}
                      style={{
                        ...(banner?.linkBackgroundColor && { backgroundColor: banner.linkBackgroundColor, borderColor: banner.linkBackgroundColor }),
                        ...(banner?.linkTextColor && { color: banner.linkTextColor }),
                      }}
                    >
                      {banner?.linkTitle}
                    </Button>
                  ),
                  title: banner?.title,
                  description: banner?.description,
                }}
                imageProps={{
                  width: properties?.imageWidth,
                  height: properties?.imageHeight,
                  src: getCloudinaryURL(banner?.media) ?? NoImageSrc,
                  alt: banner?.title ?? "Banner-content",
                  className: "w-full object-cover",
                  style: { height: `${properties?.imageHeight}px` },
                }}
                onClick={() => handleClick(banner)}
                onVisible={(isVisible) => handleOnVisible(isVisible, banner)}
              />
            ) : (
              <VideoCard
                showControls={banner?.showControls}
                url={banner?.link?.url}
                containerProps={{
                  className: cn("h-full w-full", hasOverlay && "relative"),
                  style: {
                    ...(banner?.color && { backgroundColor: banner.color }),
                    ...(hasOverlay && { background: overlayBackground }),
                  },
                }}
                videoProps={{
                  src: banner?.media,
                  className: "h-full w-full object-cover",
                  playsInline: true,
                  muted: true,
                  loop: banner?.loop,
                  title: banner?.title,
                  poster: banner?.videoThumbnail ?? NoImageSrc?.src,
                  style: { height: `${properties?.imageHeight}px` },
                }}
                action={{
                  cta: banner?.linkTitle && (
                    <Button
                      className={cn(
                        "font-semibold text-white",
                        banner?.linkVariant === "link"
                          ? "text-start text-xs md:text-sm"
                          : "flex h-[36px] min-w-[120px] items-center justify-center p-0 text-sm md:h-[50px] md:min-w-[170px]",
                      )}
                      variant={banner?.linkVariant ?? "link"}
                      style={{
                        ...(banner?.linkBackgroundColor && { backgroundColor: banner.linkBackgroundColor, borderColor: banner.linkBackgroundColor }),
                        ...(banner?.linkTextColor && { color: banner.linkTextColor }),
                      }}
                    >
                      {banner?.linkTitle}
                    </Button>
                  ),
                  title: banner?.title,
                  description: banner?.description,
                }}
                onClick={() => handleClick(banner)}
                onVisible={(isVisible) => handleOnVisible(isVisible, banner)}
              />
            )}
          </div>
        );
      }

      // MULTI COL
      if (
        [
          promotionalBannerType.TWO_COL_BANNER,
          promotionalBannerType.TWO_COL_BANNER_MOBILE,
          promotionalBannerType.THREE_COL_BANNER,
          promotionalBannerType.MIXED_THREE_COL_BANNER,
          promotionalBannerType.FOUR_COL_BANNER,
        ].includes(bannerType)
      ) {
        return (content as MultipleColBanner)?.items?.map((item, i) => {
          const key = item?.key ?? `${index}_${i}`;
          const type =
            bannerType === promotionalBannerType.MIXED_THREE_COL_BANNER
              ? `${bannerType}${item?.type}`
              : bannerType;
          const properties: PromotionalBannerProperties =
            bannerProperties[type as PromotionalBannerTypes];

          const hasOverlay = item?.bannerVariant === "overlay";
          const itemOverlayBackground = hasOverlay
            ? buildGradientOverlayCss(item?.overlay)
            : undefined;

          return (
            <div key={`multiColWrapper_${key}`} className={properties?.className}>
              {item.mediaType === mediaType.IMAGE ? (
                <ImageCard
                  url={item?.link?.url}
                  containerProps={{
                    className: cn("h-full w-full", hasOverlay && "relative"),
                    style: {
                      ...(item?.color && { backgroundColor: item.color }),
                      ...(hasOverlay && { background: itemOverlayBackground }),
                    },
                  }}
                  imageProps={{
                    alt: item?.title ?? getFieldValue(lang, "Promotional Banner", "لافتة ترويجية") ?? "",
                    src: getCloudinaryURL(item?.media) ?? NoImageSrc,
                    height: properties?.imageHeight,
                    width: properties?.imageWidth,
                    className: ` w-full object-cover`,
                    style: { height: `${properties?.imageHeight}px` },
                  }}
                  action={{
                    cta: item?.linkTitle && (
                      <Button
                        className={cn(
                          "font-semibold",
                          item?.linkVariant === "link"
                            ? "text-xs md:text-sm"
                            : "flex h-[36px] min-w-[120px] items-center justify-center p-0 text-sm md:h-[50px] md:min-w-[170px]",
                        )}
                        variant={item?.linkVariant ?? "text"}
                        style={{
                          ...(item?.linkBackgroundColor && { backgroundColor: item.linkBackgroundColor, borderColor: item.linkBackgroundColor }),
                          ...(item?.linkTextColor && { color: item.linkTextColor }),
                        }}
                      >
                        {item?.linkTitle}
                      </Button>
                    ),
                    title: item?.title,
                    description: item?.description,
                  }}
                  onClick={() => handleClick(item)}
                  onVisible={(isVisible) => handleOnVisible(isVisible, item)}
                />
              ) : (
                <VideoCard
                  key={key}
                  url={item?.link?.url}
                  showControls={item?.showControls}
                  containerProps={{
                    className: cn("h-full w-full", hasOverlay && "relative"),
                    style: {
                      aspectRatio: properties?.aspectRatio,
                      ...(item?.color && { backgroundColor: item.color }),
                      ...(hasOverlay && { background: itemOverlayBackground }),
                    },
                  }}
                  videoProps={{
                    src: item?.media,
                    className: " w-full object-cover",
                    muted: true,
                    title: item?.title,
                    loop: true,
                    poster: item?.videoThumbnail ?? NoImageSrc?.src,
                    style: { height: `${properties?.imageHeight}px` },
                  }}
                  action={{
                    cta: item?.linkTitle && (
                      <Button
                        className={cn(
                          "font-semibold text-white",
                          item?.linkVariant === "link"
                            ? "text-start text-xs md:text-sm"
                            : "flex h-[36px] min-w-[120px] items-center justify-center p-0 text-sm md:h-[50px] md:min-w-[170px]",
                        )}
                        variant={item?.linkVariant ?? "link"}
                        style={{
                          ...(item?.linkBackgroundColor && { backgroundColor: item.linkBackgroundColor, borderColor: item.linkBackgroundColor }),
                          ...(item?.linkTextColor && { color: item.linkTextColor }),
                        }}
                      >
                        {item?.linkTitle}
                      </Button>
                    ),
                    title: item?.title,
                    description: item?.description,
                  }}
                  onClick={() => handleClick(item)}
                  onVisible={(isVisible) => handleOnVisible(isVisible, item)}
                />
              )}
            </div>
          );
        });
      }

      return <></>;
    },
    [locale, handleClick, handleOnVisible],
  );

  return (
    <>
      {promotionalData?.desktop?.length > 0 ? (
        <div className="container hidden grid-cols-12 gap-6 overflow-hidden px-4 md:grid md:px-12">
          {promotionalData?.desktop?.map((item, index) => renderBanner(item, index))}
        </div>
      ) : null}

      {promotionalData?.mobile?.length > 0 && (
        <div className="container grid grid-cols-12 gap-3 overflow-hidden px-4 md:hidden">
          {promotionalData?.mobile?.map((item, index) => renderBanner(item, index))}
        </div>
      )}
    </>
  );
};

export default Promotions;

{
  /* {promotionalData.map((p, i) => {
        let containerClass = "w-full";

        if (promotionalData.length === 1) {
          // Single image spans entire row
          containerClass = "col-span-12 h-[21.438rem] sm:h-[25rem] md:h-[30.75rem]";
        } else if (promotionalData.length === 2) {
          // Two images share the row equally
          containerClass = "col-span-6 h-[21.438rem] sm:h-[25rem] md:h-[30.75rem]";
        } else if (promotionalData.length === 3) {
          // Three images: First spans entire row, next two share a row
          if (i === 0) {
            containerClass = "col-span-12 h-[21.438rem] sm:h-[25rem] md:h-[30.75rem]";
          } else {
            containerClass = "col-span-6 h-[12.438rem] sm:h-[30rem] md:h-[41.25rem]";
          }
        }

        return (
          <ImageCard
            key={`promotion_img_${i}`}
            containerProps={{ className: containerClass }}
            action={{ cta: p.cta, title: p.title, description: p.description }}
            imageProps={{
              width: 1344,
              height: 492,
              src: p.imgSrc,
              alt: `${p.title} image`,
              className: "h-full w-full",
            }}
          />
        );
      })} */
}